/**
 * AgentPublicProfile — Pagina pubblica `/agente/:slug`.
 *
 * Mostra il profilo dell'agente con i suoi immobili attivi in 4 stili
 * (elegante / luxury / moderno / minimal) coerenti con la vetrina immobile.
 * I font/tono vengono dai LAYOUTS in `staticTemplate/tokens.js` — stessi
 * dell'editor social, così il branding resta lo stesso ovunque.
 *
 * iter v32 r36 · User M1428: pagina pubblica per ogni agente, condivisibile
 * via bio Instagram / WhatsApp / firma email.
 *
 * iter v32 r37r · User M1448: fix caricamento foto (path raw del storage
 * non funzionavano), tolto duplicato nome agenzia, aggiunta sezione Servizi
 * e barra contatti sticky mobile sempre visibile.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  EnvelopeSimple, MapPin, Phone, Globe, ArrowRight, Buildings, HouseLine,
  WhatsappLogo, SealCheck, X, Sparkle, Key, MagnifyingGlass, Scales, Handshake,
} from "@phosphor-icons/react";

import { LAYOUTS, COLORS, readableTextOn } from "@/components/staticTemplate/tokens";

// iter v33 r6 — User M1483: «il tuo sito online mi esce sempre caricamento
// in corso». Root cause: si usava `process.env.REACT_APP_BACKEND_URL`
// bakeato al build time (= `desktop-suzuka.emergent.host`) → tutte le
// richieste cross-origin verso quel dominio. Su webview iOS l'ITP le
// bloccava silente, axios restava pending → schermata di caricamento
// infinita. Fix: `API` viene da `lib/api.js` che ha `detectBackendUrl`
// (usa `prontoimmo.it` su Capacitor e su prontoimmo.it web = SAME ORIGIN,
// niente CORS, niente ITP).
import { API } from "@/lib/api";
const BACKEND = API.replace(/\/api$/, "");

/**
 * Trasforma un URL relativo `/api/...` in URL assoluta usando
 * REACT_APP_BACKEND_URL. Le foto agente/immobile dal backend arrivano
 * SEMPRE come path relativi (`/api/public/agency/.../agent-photo`) per
 * essere portable tra env preview/prod.
 */
const absUrl = (u) => {
  if (!u) return null;
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/")) return `${BACKEND}${u}`;
  return u;
};

/** Iniziali per fallback senza foto. */
function initialsOf(name) {
  if (!name) return "·";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "·";
  return ((parts[0][0] || "") + (parts[parts.length - 1][0] || "")).toUpperCase();
}

const fmtPrice = (n) => {
  if (n == null) return "—";
  try {
    return new Intl.NumberFormat("it-IT", {
      style: "currency", currency: "EUR", maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `€ ${n}`;
  }
};

const buildWa = (mobile) => {
  if (!mobile) return null;
  const clean = mobile.replace(/[^+0-9]/g, "");
  return `https://wa.me/${clean.replace(/^\+/, "")}`;
};

/**
 * iter v32 r37r · User M1448 — Mostra agency_name SOLO se diverso dal
 * nome dell'agente (altrimenti duplica visivamente "Gianfilippo Baio ·
 * GIANFILIPPO BAIO" come da segnalazione).
 */
const displayAgency = (data) => {
  const ag = (data.agency_name || "").trim();
  const nm = (data.agent_display_name || "").trim();
  if (!ag) return null;
  if (ag.toLowerCase() === nm.toLowerCase()) return null;
  return ag;
};

/**
 * iter v32 r48 · User M1445 — Sotto al nome agente, riga di qualifica:
 *  · se ha un'agenzia diversa dal proprio nome → "Agente immobiliare per X"
 *  · se l'agenzia ha il suo stesso nome (agenzia propria) → "Agente immobiliare"
 *  · se non ha un'agenzia configurata → "Agente immobiliare"
 *
 * iter v32 r49g · User M1454 — Prima quando agenzia == nome ritornavamo
 * l'agency stesso → si vedeva "GIANFILIPPO BAIO" sopra e "GIANFILIPPO
 * BAIO" sotto, duplicato visivo. Ora in quel caso scriviamo sempre
 * "Agente immobiliare", che è la sua qualifica.
 */
const agencyTagline = (data) => {
  const ag = (data.agency_name || "").trim();
  const nm = (data.agent_display_name || "").trim();
  if (!ag) return "Agente immobiliare";
  if (ag.toLowerCase() === nm.toLowerCase()) return "Agente immobiliare";
  return `Agente immobiliare per ${ag}`;
};

/** Servizi predefiniti — uguali per ogni agente sulla vetrina.
 *  iter v33 r64 · User: «troppo testo, uno si perde via, deve essere
 *  piu' immediato». Rimosse le descrizioni prolisse: sulla vetrina
 *  pubblica l'occhio del cliente scorre veloce, un'icona + il nome
 *  del servizio comunicano quanto serve, il resto e' rumore. */
const DEFAULT_SERVICES = [
  { Icon: HouseLine, title: "Vendita" },
  { Icon: Key,       title: "Affitto" },
  { Icon: Scales,    title: "Valutazione gratuita" },
  { Icon: Handshake, title: "Consulenza acquisto" },
];

export default function AgentPublicProfile() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null); setError(null);
    axios
      .get(`${API}/public/agente/${slug}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        const code = e?.response?.status;
        setError(code === 404 ? "not_found" : "load_error");
      });
    return () => { cancelled = true; };
  }, [slug]);

  // iter v32 r37u · User M1453 — Aggiorna document.title + meta description
  // + OG tags dinamicamente quando `data` cambia. Aiuta browser tab,
  // search engines che eseguono JS, e crawler social moderni.
  useEffect(() => {
    if (!data) return;
    const name = data.agent_display_name || "Agente";
    const agency = data.agency_name && data.agency_name !== name ? data.agency_name : null;
    const subtitle = agency
      ? `${agency} · Immobili in vendita e in affitto`
      : `${data.properties_count || "Immobili"} immobili curati · sito personale su Pronto`;
    const title = `${name} · Il sito personale dell'agente immobiliare`;
    const description = data.bio || subtitle;
    // iter v33 r20 · User M1495 — Prima usavamo `process.env.REACT_APP_BACKEND_URL`
    // bakeato al build (= dominio preview) per prefissare la og:image. Ora
    // usiamo `absUrl()` che risolve dinamicamente contro l'origine corrente
    // (prontoimmo.it in prod, preview.emergentagent.com in staging). Cosi'
    // WhatsApp/Facebook/LinkedIn scaricano la foto dal dominio giusto.
    const photoUrl = absUrl(data.agent_photo_url);

    document.title = title;

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = selector.replace("meta[", "").replace("]", "").split("=");
        el.setAttribute(k, v.replace(/['"]/g, ""));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta(`meta[name="description"]`, "content", description);
    setMeta(`meta[property="og:title"]`, "content", title);
    setMeta(`meta[property="og:description"]`, "content", description);
    setMeta(`meta[property="og:type"]`, "content", "profile");
    setMeta(`meta[property="og:url"]`, "content", window.location.href);
    if (photoUrl) setMeta(`meta[property="og:image"]`, "content", photoUrl);
    setMeta(`meta[name="twitter:card"]`, "content", photoUrl ? "summary_large_image" : "summary");
    setMeta(`meta[name="twitter:title"]`, "content", title);
    setMeta(`meta[name="twitter:description"]`, "content", description);
  }, [data]);

  if (error === "not_found") {
    return <NotFound />;
  }
  if (error === "load_error") {
    return <LoadError />;
  }
  if (!data) {
    return <LoadingShell />;
  }

  const palette = COLORS?.[data.brand_color]
    || Object.values(COLORS || {}).find((c) => c?.swatch?.toLowerCase() === (data.brand_color || "").toLowerCase())
    || { bg: "#FAFAF7", fg: "#1C1C1A", accent: "#7C8B6E", border: "rgba(28,28,26,0.16)", isDark: false };

  // iter r96e · User M1513 — Query param `?style=elegante|luxury|moderno|minimal`
  // per previewere le 4 skin senza cambiare il DB. Utile quando l'agente
  // vuole valutare quale stile applicare. Se il param manca, usa lo style
  // salvato nel DB. Solo override visivo, non modifica preferenze utente.
  const urlStyle = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("style")
    : null;
  const validStyles = ["elegante", "luxury", "moderno", "minimal"];
  const styleId = (urlStyle && validStyles.includes(urlStyle))
    ? urlStyle
    : (data.style || "elegante");
  const layout = LAYOUTS[styleId] || LAYOUTS.elegante;

  // iter r96f · User M1514 — Sfondo pagina indipendente dallo skin.
  // Se l'utente ha scelto un `page_bg` esplicito (non "auto"), lo usiamo
  // come override sul bg hardcoded della skin. Il testo si aggiusta di
  // conseguenza (chiaro su nero, scuro su cream/white/grey).
  // Anche via query param `?bg=cream|white|black|light-grey` per preview.
  const urlBg = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("bg")
    : null;
  const validBg = ["cream", "white", "black", "light-grey"];
  const pageBgKey = (urlBg && validBg.includes(urlBg))
    ? urlBg
    : (data.page_bg && validBg.includes(data.page_bg) ? data.page_bg : null);
  const PAGE_BG_MAP = {
    cream:        { bg: "#F5F1EA", fg: "#1A1817", isDark: false },
    white:        { bg: "#FFFFFF", fg: "#1C1C1A", isDark: false },
    "light-grey": { bg: "#EDEDEA", fg: "#1C1C1A", isDark: false },
    black:        { bg: "#0A0A0A", fg: "#FAF6EE", isDark: true  },
  };
  const bgOverride = pageBgKey ? PAGE_BG_MAP[pageBgKey] : null;
  return (
    <div
      className="min-h-screen pb-24 sm:pb-0"
      style={{
        background: palette.bg,
        color: palette.fg,
        fontFamily: layout.bodyFont,
      }}
      data-testid="agent-public-profile"
      data-style={styleId}
    >
      {styleId === "elegante" && <SkinElegante data={data} layout={layout} palette={palette} bgOverride={bgOverride} />}
      {styleId === "luxury"   && <SkinLuxury   data={data} layout={layout} palette={palette} bgOverride={bgOverride} />}
      {styleId === "moderno"  && <SkinModerno  data={data} layout={layout} palette={palette} bgOverride={bgOverride} />}
      {styleId === "minimal"  && <SkinMinimal  data={data} layout={layout} palette={palette} bgOverride={bgOverride} />}

      {/* iter v32 r37d · CTA sticky per agenti non-clienti che capitano sul
          profilo pubblico. Visibile solo se l'utente non è loggato a Pronto
          e non ha già chiuso il banner. */}
      <PublicProfileCTA agentName={data.agent_display_name} />

      {/* iter v32 r37r · User M1448 — Barra contatti sticky mobile sempre
          visibile durante lo scroll. Telefono + WhatsApp + Email a portata
          di pollice. Su desktop nascosta (i contatti sono già nel hero). */}
      <MobileContactBar data={data} palette={palette} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   LAYOUT 1 · ELEGANTE — Hero verticale, foto colonna SX + nome XL DX,
   griglia editoriale a 2 col con accenti color brand.
   ═══════════════════════════════════════════════════════════════════ */
function SkinElegante({ data, layout, palette, bgOverride }) {
  const agency = displayAgency(data);
  const logo = absUrl(data.agency_logo_url);
  // iter v32 r37x ROUND 2 · brief design — EDITORIAL.
  // Magazine Cuyana/Aritzia/Kinfolk. Warm alabaster (#F5F1EA), foto 3:4
  // ampia a destra, nome serif italic gigante a sinistra, whitespace
  // generosissimo. ZERO blocchi pieni di colore brand.
  return (
    <>
      <section
        className="relative"
        style={{ background: bgOverride?.bg || "#F5F1EA", color: bgOverride?.fg || "#1A1817" }}
        data-testid="elegante-hero"
      >
        {/* Top thin micro-header */}
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-8 sm:pt-12 flex items-center justify-between text-[10.5px] uppercase tracking-[0.32em] font-semibold" style={{ color: "#6E665C" }}>
          {logo ? (
            <img
              src={logo}
              alt={agency || ""}
              className="object-contain"
              style={{ height: 26, maxWidth: 130, filter: "brightness(0)", opacity: 0.78 }}
            />
          ) : agency ? (
            <div>{agency}</div>
          ) : <div />}
          <div>{data.agency_address ? data.agency_address.split(",").pop().trim() : ""}</div>
        </div>

        {/* Hero split asimmetrico */}
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-10 sm:pt-16 pb-20 sm:pb-32">
          <div className="grid grid-cols-12 gap-6 sm:gap-12 items-end">
            {/* Testo a sx (su mobile sopra) */}
            <div className="col-span-12 sm:col-span-7 order-2 sm:order-1">
              <div
                className="text-[11px] uppercase tracking-[0.36em] font-semibold mb-6"
                style={{ color: palette.accent }}
              >
                Vetrina · {data.properties_count} immobili
              </div>
              <h1
                className="leading-[0.92]"
                style={{
                  fontFamily: layout.headingFont,
                  fontStyle: "italic",
                  fontWeight: 400,
                  letterSpacing: "-0.02em",
                  color: "#1A1817",
                  fontSize: "clamp(48px, 12vw, 116px)",
                }}
                data-testid="agent-name"
              >
                {data.agent_display_name}
              </h1>

              {/* iter v32 r48 · qualifica sotto il nome — riempie la vetrina */}
              <div
                className="mt-4 text-[15px] sm:text-[17px]"
                style={{
                  fontFamily: layout.headingFont,
                  fontStyle: "italic",
                  fontWeight: 300,
                  color: "#6E665C",
                  letterSpacing: "0.01em",
                }}
                data-testid="agent-tagline"
              >
                {agencyTagline(data)}
              </div>

              {data.bio && (
                <p
                  className="mt-8 max-w-md leading-[1.55]"
                  style={{
                    fontFamily: layout.headingFont,
                    fontStyle: "italic",
                    fontSize: "20px",
                    color: "#3A3531",
                    fontWeight: 300,
                  }}
                >
                  {data.bio}
                </p>
              )}

              <div className="mt-10 -ml-1">
                <ContactRow data={data} palette={{ ...palette, fg: "#1A1817" }} />
              </div>
            </div>

            {/* Foto agente a dx — modernizzata r96e: rounded-2xl, ombra sottile pulita */}
            <div className="col-span-12 sm:col-span-5 order-1 sm:order-2">
              <div className="w-full mx-auto sm:mx-0" style={{ maxWidth: 240 }}>
                <AgentPhoto data={data} palette={palette} aspect="3/4" rounded="rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider palette={palette} label={`${data.properties_count} immobili in vetrina`} accentLine soldBadge={data.sold_last_12m} />

      {/* PROPERTIES */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-10 pb-12 sm:pb-20">
        {data.properties.length === 0 ? (
          <EmptyState palette={palette} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {data.properties.map((p) => (
              <PropertyCardEditorial key={p.id} p={p} palette={palette} layout={layout} />
            ))}
          </div>
        )}
      </section>

      <ServicesBlock palette={palette} layout={layout} />

      <ContactFooter data={data} palette={palette} layout={layout} agencyLogo={logo} />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   LAYOUT 2 · LUXURY — Cover rivista cream, foto agente "framed",
   immobili come articoli con numerazione.
   ═══════════════════════════════════════════════════════════════════ */
function SkinLuxury({ data, layout, palette, bgOverride }) {
  const agency = displayAgency(data);
  const logo = absUrl(data.agency_logo_url);
  // iter v32 r37x ROUND 4 · brief design — SOTHEBY'S / CHRISTIE'S sobrio.
  // BG ivory/cream pieno, hero a 2 colonne con foto agente in cornice
  // sottile dorata, nome Spectral italic, abbondante whitespace.
  // NIENTE cinematic full-bleed (l'utente l'ha definito "burinata").
  return (
    <>
      <section
        className="relative"
        style={{ background: bgOverride?.bg || "#F4EFE5", color: bgOverride?.fg || "#1A1715" }}
        data-testid="luxury-hero"
      >
        {/* Top thin header — logo agenzia centrato */}
        {(logo || agency) && (
          <div className="border-b" style={{ borderColor: "rgba(26,23,21,0.10)" }}>
            <div className="max-w-5xl mx-auto px-6 sm:px-10 py-5 sm:py-6 flex items-center justify-center">
              {logo ? (
                <img
                  src={logo}
                  alt={agency || ""}
                  className="object-contain"
                  style={{ height: 32, maxWidth: 150, filter: "brightness(0)", opacity: 0.78 }}
                  data-testid="luxury-agency-logo"
                />
              ) : (
                <div className="text-[10.5px] uppercase tracking-[0.4em] font-semibold" style={{ color: "#7A6E60" }}>
                  {agency}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Hero compatto 2 colonne sobrio */}
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-12 sm:py-20">
          <div className="grid grid-cols-12 gap-7 sm:gap-12 items-center">
            {/* Foto agente — modernizzata r96e: rimossa cornice oro vintage
                che rendeva l'insieme "santino della morte" (feedback User M1513).
                r96f · rimossa anche sfumatura pesante dietro (feedback M1516). */}
            <div className="col-span-12 sm:col-span-5">
              <div className="w-full mx-auto" style={{ maxWidth: 260 }}>
                <AgentPhoto data={data} palette={palette} aspect="3/4" rounded="rounded-2xl" />
              </div>
            </div>

            {/* Info — testo a destra */}
            <div className="col-span-12 sm:col-span-7 text-center sm:text-left">
              <div
                className="text-[10px] uppercase tracking-[0.36em] font-semibold mb-4"
                style={{ color: "#C9B27A" }}
              >
                Vetrina · {data.properties_count} immobili
              </div>
              <h1
                className="leading-[0.96] mb-4"
                style={{
                  fontFamily: layout.headingFont,
                  fontStyle: "italic",
                  fontWeight: 400,
                  fontSize: "clamp(38px, 9.5vw, 68px)",
                  letterSpacing: "-0.012em",
                  color: "#1A1715",
                }}
                data-testid="agent-name"
              >
                {data.agent_display_name}
              </h1>

              {/* iter v32 r48 · qualifica sotto il nome — luxury */}
              <div
                className="mb-4 text-[14px] sm:text-[15px] uppercase"
                style={{
                  fontFamily: "'Inter','Helvetica Neue',sans-serif",
                  fontWeight: 500,
                  color: "#6E5F47",
                  letterSpacing: "0.18em",
                }}
                data-testid="agent-tagline"
              >
                {agencyTagline(data)}
              </div>

              {/* divider classico — linea·dot·linea */}
              <div className="flex items-center gap-3 mb-5 justify-center sm:justify-start" aria-hidden>
                <span className="h-px w-10" style={{ background: "#C9B27A", opacity: 0.7 }} />
                <span style={{ color: "#C9B27A", fontFamily: layout.headingFont, fontStyle: "italic", fontSize: 16 }}>·</span>
                <span className="h-px w-10" style={{ background: "#C9B27A", opacity: 0.7 }} />
              </div>

              {data.bio && (
                <p
                  className="text-[15px] sm:text-[16px] leading-[1.6] mb-6 mx-auto sm:mx-0"
                  style={{
                    fontFamily: layout.headingFont,
                    fontStyle: "italic",
                    fontWeight: 300,
                    color: "#3A3531",
                    maxWidth: 380,
                  }}
                >
                  {data.bio}
                </p>
              )}

              <div className="flex justify-center sm:justify-start -ml-1">
                <ContactRow data={data} palette={{ ...palette, fg: "#1A1715" }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider palette={palette} label={`${data.properties_count} immobili in vetrina`} accentLine soldBadge={data.sold_last_12m} />

      {/* PROPERTIES — magazine style */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-12 sm:pb-20">
        {data.properties.length === 0 ? (
          <EmptyState palette={palette} />
        ) : (
          <div className="space-y-12 sm:space-y-16">
            {data.properties.map((p, i) => (
              <PropertyCardMagazine key={p.id} p={p} idx={i + 1} palette={palette} layout={layout} />
            ))}
          </div>
        )}
      </section>

      <ServicesBlock palette={palette} layout={layout} variant="luxury" />

      <ContactFooter data={data} palette={palette} layout={layout} agencyLogo={logo} />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   LAYOUT 3 · MODERNO — Split hero geometrico, bento asimmetrico per
   gli immobili, accenti contrastati.
   ═══════════════════════════════════════════════════════════════════ */
function SkinModerno({ data, layout, palette, bgOverride }) {
  const agency = displayAgency(data);
  const logo = absUrl(data.agency_logo_url);
  // iter v32 r37x ROUND 2 · brief design — LINEAR / VERCEL / APPLE NEWS.
  // Bg nero profondo (#0A0A0A), foto agente 4:5 (60vh), nome Archivo
  // Black gigante. Brand color SOLO come line 2px accent puntuale.
  return (
    <>
      <section
        className="relative"
        style={{ background: bgOverride?.bg || "#0A0A0A", color: bgOverride?.fg || "#FAF6EE" }}
        data-testid="moderno-hero"
      >
        {/* Top header thin */}
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-7 sm:pt-10 flex items-center justify-between">
          {logo ? (
            <img
              src={logo}
              alt={agency || ""}
              className="object-contain"
              style={{ height: 32, maxWidth: 160, filter: "brightness(0) invert(1)", opacity: 0.9 }}
            />
          ) : agency ? (
            <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-white/80">
              {agency}
            </div>
          ) : <div />}
          <div className="text-[10.5px] uppercase tracking-[0.32em] font-medium text-white/55">
            {data.agency_address ? data.agency_address.split(",").pop().trim() : ""}
          </div>
        </div>

        {/* Hero split asimmetrico — testo sx, foto dx */}
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-10 sm:pt-14 pb-16 sm:pb-24">
          <div className="grid grid-cols-12 gap-6 sm:gap-10 items-center">
            {/* Testo sx */}
            <div className="col-span-12 sm:col-span-7 order-2 sm:order-1">
              {/* Status dot + label */}
              <div className="flex items-center gap-2.5 mb-6">
                <span
                  className="inline-block rounded-full"
                  style={{ width: 8, height: 8, background: palette.accent, boxShadow: `0 0 0 4px ${palette.accent}22` }}
                />
                <span className="text-[11px] uppercase tracking-[0.32em] font-semibold text-white/70">
                  Disponibile · {data.properties_count} immobili
                </span>
              </div>

              <h1
                className="text-white"
                style={{
                  fontFamily: "'Archivo','Inter',sans-serif",
                  fontWeight: 900,
                  // iter v32 r49g · User M1454 — «in moderno è troppo grande,
                  // su mobile va a capo». Ridotto il range: 36px base /
                  // 9vw scaling / 96px cap. Resta statement, ma "Gianfilippo
                  // Baio" sta su una riga sola anche su iPhone SE.
                  fontSize: "clamp(36px, 9vw, 96px)",
                  lineHeight: 0.92,
                  letterSpacing: "-0.035em",
                  textTransform: "uppercase",
                }}
                data-testid="agent-name"
              >
                {data.agent_display_name}
              </h1>

              {/* iter v32 r48 · qualifica sotto il nome — moderno */}
              <div
                className="mt-4 text-[13px] sm:text-[14px] uppercase text-white/70"
                style={{
                  fontFamily: "'Inter','Helvetica Neue',sans-serif",
                  fontWeight: 600,
                  letterSpacing: "0.24em",
                }}
                data-testid="agent-tagline"
              >
                {agencyTagline(data)}
              </div>

              {/* Line 2px accent brand — l'unico vero pop di colore */}
              <div className="mt-7 h-0.5 w-16" style={{ background: palette.accent }} />

              {data.bio && (
                <p
                  className="mt-7 text-[15px] sm:text-[17px] text-white/72 max-w-md leading-relaxed"
                  style={{ fontFamily: "'Inter',sans-serif" }}
                >
                  {data.bio}
                </p>
              )}

              <div className="mt-10 -ml-1">
                <ContactRow data={data} palette={{ ...palette, fg: "#FAF6EE", accent: palette.accent }} ink />
              </div>
            </div>

            {/* Foto agente 4:5 a destra — modernizzata r96e: rounded-2xl, più grande.
                r96f · rimosso glow accent lilla dietro (feedback M1516). */}
            <div className="col-span-12 sm:col-span-5 order-1 sm:order-2">
              <div
                className="w-full mx-auto sm:mx-0 overflow-hidden"
                style={{
                  maxWidth: 260,
                  aspectRatio: "4/5",
                }}
              >
                <AgentPhoto data={data} palette={palette} aspect="4/5" rounded="rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider palette={palette} label={`${data.properties_count} immobili in vetrina`} accentLine soldBadge={data.sold_last_12m} />

      {/* PROPERTIES bento */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-10 pb-12 sm:pb-20">
        {data.properties.length === 0 ? (
          <EmptyState palette={palette} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            {data.properties.map((p, i) => (
              <PropertyCardBento
                key={p.id}
                p={p}
                palette={palette}
                layout={layout}
                isHero={i === 0 || (i > 0 && i % 5 === 0)}
              />
            ))}
          </div>
        )}
      </section>

      <ServicesBlock palette={palette} layout={layout} variant="moderno" />

      <ContactFooter data={data} palette={palette} layout={layout} agencyLogo={logo} />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   LAYOUT 4 · MINIMAL — Hero centrato, foto piccola, tipografia pulita,
   lista immobili verticale con foto grandi singole.
   ═══════════════════════════════════════════════════════════════════ */
function SkinMinimal({ data, layout, palette, bgOverride }) {
  const agency = displayAgency(data);
  const logo = absUrl(data.agency_logo_url);
  // iter v32 r37x ROUND 2 · brief design — NOTION / ReadCV personal page.
  // Bianco puro, flush-left, niente decorazioni. Foto avatar piccola
  // top-left. Accent brand color SOLO su link underline.
  const phoneTel = data.agent_mobile ? `tel:${data.agent_mobile.replace(/[^+0-9]/g, "")}` : null;
  const wa = buildWa(data.agent_mobile);
  const mail = data.agency_email ? `mailto:${data.agency_email}` : null;
  return (
    <>
      <section
        style={{ background: bgOverride?.bg || "#FFFFFF", color: bgOverride?.fg || "#1C1C1A" }}
        data-testid="minimal-hero"
      >
        <div className="max-w-2xl mx-auto px-6 sm:px-8 pt-10 sm:pt-16 pb-10">
          {/* Header 2-col: sx avatar+info, dx numero immobili gigante
              r96e — avatar più grande (96px) con soft ring accent per fare
              da hero anchor, non solo micro badge. */}
          <div className="flex items-start justify-between gap-6 mb-7">
            <div
              className="flex-shrink-0"
              style={{
                width: 96,
                height: 96,
                padding: 3,
                borderRadius: "24px",
                background: `${palette.accent}18`,
              }}
            >
              <AgentPhoto data={data} palette={palette} aspect="1/1" rounded="rounded-2xl" />
            </div>
            <div className="text-right">
              <div
                className="leading-none text-neutral-900"
                style={{
                  fontFamily: "'JetBrains Mono','SF Mono',monospace",
                  fontWeight: 700,
                  fontSize: "44px",
                  letterSpacing: "-0.04em",
                }}
                data-testid="minimal-prop-count"
              >
                {String(data.properties_count).padStart(2, "0")}
              </div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-neutral-500 mt-1.5">
                Immobili
              </div>
            </div>
          </div>

          {/* Nome */}
          <h1
            className="text-[34px] sm:text-[42px] leading-[1.05] mb-2 text-neutral-900"
            style={{
              fontFamily: "'Space Grotesk','Inter',sans-serif",
              fontWeight: 600,
              letterSpacing: "-0.02em",
            }}
            data-testid="agent-name"
          >
            {data.agent_display_name}
          </h1>

          {/* iter v32 r48 · qualifica sotto il nome — minimal */}
          <div
            className="text-[14px] sm:text-[15px] text-neutral-600 mb-3"
            style={{ fontFamily: "'Space Grotesk','Inter',sans-serif", fontWeight: 400 }}
            data-testid="agent-tagline"
          >
            {agencyTagline(data)}
          </div>

          {/* Agenzia */}
          {logo && (
            <div className="flex items-center gap-2 mb-3">
              <img
                src={logo}
                alt={agency || ""}
                className="object-contain"
                style={{ height: 18, maxWidth: 100, filter: "brightness(0)" }}
              />
            </div>
          )}

          {/* Indirizzo */}
          {data.agency_address && (
            <div className="text-[13px] text-neutral-500 mb-7 flex items-center gap-1.5">
              <MapPin size={12} weight="regular" />
              {data.agency_address}
            </div>
          )}

          {/* Bio */}
          {data.bio && (
            <p
              className="text-[15.5px] leading-[1.62] text-neutral-700 max-w-xl mb-8"
              style={{ fontFamily: "'Space Grotesk','Inter',sans-serif" }}
            >
              {data.bio}
            </p>
          )}

          {/* Lista contatti flush-left con underline accent */}
          <div className="space-y-2.5 mb-8">
            {phoneTel && (
              <a
                href={phoneTel}
                className="block text-[15px] text-neutral-900 hover:opacity-70"
                style={{ borderBottom: `1.5px solid ${palette.accent}`, paddingBottom: 2, display: "inline-block", marginRight: 14 }}
                data-testid="minimal-link-tel"
              >
                {data.agent_mobile}
              </a>
            )}
            {wa && (
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[15px] text-neutral-900 hover:opacity-70"
                style={{ borderBottom: `1.5px solid ${palette.accent}`, paddingBottom: 2, display: "inline-block", marginRight: 14 }}
                data-testid="minimal-link-wa"
              >
                WhatsApp
              </a>
            )}
            {mail && (
              <a
                href={mail}
                className="text-[15px] text-neutral-900 hover:opacity-70 break-all"
                style={{ borderBottom: `1.5px solid ${palette.accent}`, paddingBottom: 2, display: "inline-block" }}
                data-testid="minimal-link-email"
              >
                {data.agency_email}
              </a>
            )}
          </div>

          {/* Stats mono-spaced inline (2 cols, no duplicato col numero sopra) */}
          <div className="border-t border-neutral-200 pt-7 grid grid-cols-2 gap-6">
            <Stat value={data.years_experience || "—"} label={data.years_experience ? "Anni di esperienza" : "Esperienza"} palette={palette} layout={{ ...layout, headingFont: "'JetBrains Mono','SF Mono',monospace", titleStyle: {} }} />
            <Stat value={data.sold_last_12m > 0 ? data.sold_last_12m : "—"} label={data.sold_last_12m > 0 ? "Venduti (12 mesi)" : "Stima gratis casa"} palette={palette} layout={{ ...layout, headingFont: "'JetBrains Mono','SF Mono',monospace", titleStyle: {} }} />
          </div>
        </div>
      </section>

      <SectionDivider palette={palette} label={`${data.properties_count} immobili in vetrina`} accentLine soldBadge={data.sold_last_12m} />

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-12 sm:pb-20">
        {data.properties.length === 0 ? (
          <EmptyState palette={palette} />
        ) : (
          <div className="space-y-12">
            {data.properties.map((p) => (
              <PropertyCardMinimal key={p.id} p={p} palette={palette} layout={layout} />
            ))}
          </div>
        )}
      </section>

      <ServicesBlock palette={palette} layout={layout} variant="minimal" />

      <ContactFooter data={data} palette={palette} layout={layout} agencyLogo={logo} />
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   SUB-COMPONENTI · cards & shared
   ───────────────────────────────────────────────────────────────────── */

// iter v32 r49g · User M1454 — Catena di fallback per la foto agente:
//   1) Foto profilo dell'agente (se caricata)
//   2) Logo dell'agenzia (se caricato, senza box/cornice — è già un logo)
//   3) Iniziali del nome (es. "GB" per Gianfilippo Baio)
// Prima saltavamo direttamente alla 3 quando mancava la foto: brutto sui
// vetrine dove l'agente ha caricato il logo ma non si è fatto un ritratto.
//
// iter v33 r20 · User M1495 — «l'avatar sulla vetrina mi esce sempre "?"».
// Root cause: sul WebView iOS la <img> falliva silente (onError non
// sempre triggerava) e il browser mostrava la sua icona placeholder
// nativa "?" al posto della foto, senza attivare il fallback iniziali.
// Fix: renderizziamo SEMPRE le iniziali dietro come sfondo persistente,
// poi carichiamo la <img> in overlay che si mostra SOLO dopo `onLoad`
// (opacity 0→1 con transizione). Se `onError` scatta O la img non
// carica entro il timeout, resta visibile lo strato iniziali/logo.
function AgentPhoto({ data, palette, aspect = "3/4", rounded = "rounded-md", fillContainer = false }) {
  const [photoErrored, setPhotoErrored] = useState(false);
  const [photoLoaded, setPhotoLoaded] = useState(false);
  const photoSrc = photoErrored ? null : absUrl(data.agent_photo_url);
  const showPhoto = photoSrc && !photoErrored;

  // iter v33 r20 · User M1495 — Safety net per iOS WebView "silent hang":
  // se la <img> non triggera ne' onLoad ne' onError entro 6s, forziamo
  // photoErrored=true cosi' cadiamo sulle iniziali. Prima l'utente vedeva
  // un placeholder "?" nativo del browser che restava li per sempre.
  useEffect(() => {
    if (!showPhoto || photoLoaded) return undefined;
    const t = setTimeout(() => setPhotoErrored(true), 6000);
    return () => clearTimeout(t);
  }, [showPhoto, photoLoaded, photoSrc]);

  return (
    <div
      className={`${rounded} overflow-hidden w-full relative ${fillContainer ? "h-full" : ""}`}
      style={{
        aspectRatio: fillContainer ? undefined : aspect,
      }}
      data-testid="agent-photo"
    >
      {/* Strato 0 — SEMPRE presente dietro: iniziali. Cosi' se img fallisce
          silente sul WebView iOS, l'utente vede iniziali eleganti al posto
          del "?" placeholder nativo del browser.
          iter r96d · User M1512 — Rimosso strato "logo agenzia" come fallback
          della foto: su un sito PERSONALE il logo agenzia al posto della
          foto è fuori posto. Se manca la foto → iniziali eleganti. Basta. */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          background: palette.isDark
            ? `linear-gradient(${palette.accent}22, ${palette.accent}22), #2A2725`
            : `linear-gradient(${palette.accent}14, ${palette.accent}14), ${palette.bg || "#F4EFE5"}`,
          containerType: "inline-size",
        }}
        data-testid="agent-photo-initials"
      >
        <span
          style={{
            fontFamily: "Bodoni Moda, serif",
            fontSize: "clamp(24px, 34cqi, 72px)",
            fontStyle: "italic",
            fontWeight: 400,
            color: palette.fg,
            opacity: 0.55,
            letterSpacing: "-0.02em",
            lineHeight: 1,
            textTransform: "uppercase",
          }}
        >
          {initialsOf(data.agent_display_name)}
        </span>
      </div>

      {/* Strato 1 — Foto agente. Diventa visibile SOLO dopo il caricamento
          effettivo (onLoad). Se onError scatta o non carica, resta trasparente
          e vediamo lo strato sotto (iniziali). */}
      {showPhoto && (
        <img
          src={photoSrc}
          alt={data.agent_display_name}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
          style={{ opacity: photoLoaded ? 1 : 0 }}
          loading="lazy"
          onLoad={() => setPhotoLoaded(true)}
          onError={() => setPhotoErrored(true)}
        />
      )}
    </div>
  );
}

function ContactRow({ data, palette, centered = false, ink = false }) {
  const wa = buildWa(data.agent_mobile);
  const items = [
    data.agent_mobile && {
      href: `tel:${data.agent_mobile.replace(/[^+0-9]/g, "")}`,
      label: data.agent_mobile, Icon: Phone, key: "phone",
    },
    wa && { href: wa, label: "WhatsApp", Icon: WhatsappLogo, key: "wa", external: true },
    data.agency_email && { href: `mailto:${data.agency_email}`, label: data.agency_email, Icon: EnvelopeSimple, key: "email" },
    data.agency_website && {
      href: data.agency_website.startsWith("http") ? data.agency_website : `https://${data.agency_website}`,
      label: data.agency_website.replace(/^https?:\/\//, ""), Icon: Globe, key: "web", external: true,
    },
  ].filter(Boolean);
  if (items.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-2.5 ${centered ? "justify-center" : ""}`}>
      {items.map(({ href, label, Icon, key, external }) => (
        <a
          key={key}
          href={href}
          target={external ? "_blank" : undefined}
          rel={external ? "noopener noreferrer" : undefined}
          // iter v32 r37t · User M1450 — Tutti i bottoni contatto in forma
          // pill (oval) coerente: rounded-full ovunque.
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12.5px] sm:text-[13px] font-medium transition-all hover:scale-[1.03] active:scale-95"
          style={{
            background: ink ? "rgba(255,255,255,0.10)" : palette.bg,
            color: palette.fg,
            border: `1px solid ${ink ? "rgba(255,255,255,0.18)" : palette.border}`,
          }}
          data-testid={`contact-${key}`}
        >
          <Icon size={14} weight="regular" style={{ color: palette.accent }} />
          <span className="truncate max-w-[160px] sm:max-w-[200px]">{label}</span>
        </a>
      ))}
    </div>
  );
}

function PropertyLink({ p, children, className, style }) {
  // iter v32 r37t · User M1450 — Solo `/listing/{public_id}` (canonical).
  // Il backend auto-crea il listing se mancante, quindi public_id sarà
  // sempre disponibile quando l'immobile arriva qui.
  const href = p.public_id ? `/listing/${p.public_id}` : null;
  if (!href) {
    return (
      <div className={className} style={style} data-testid={`property-card-${p.id}`}>
        {children}
      </div>
    );
  }
  return (
    <Link to={href} className={className} style={style} data-testid={`property-card-${p.id}`}>
      {children}
    </Link>
  );
}

function PropertyCover({ p, palette, aspect = "16/10", rounded = "rounded-md" }) {
  const [errored, setErrored] = useState(false);
  const src = errored ? null : absUrl(p.cover_photo_url);
  if (!src) {
    return (
      <div
        className={`w-full ${rounded} flex items-center justify-center overflow-hidden`}
        style={{ aspectRatio: aspect, background: palette.border }}
      >
        <HouseLine size={32} weight="light" style={{ opacity: 0.35 }} />
      </div>
    );
  }
  return (
    <div
      className={`w-full ${rounded} overflow-hidden`}
      style={{ aspectRatio: aspect, background: palette.border }}
    >
      <img
        src={src}
        alt={p.title || "Immobile"}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
        onError={() => setErrored(true)}
      />
    </div>
  );
}

function PropertyMeta({ p, palette }) {
  const bits = [
    p.sqm && `${p.sqm} m²`,
    p.rooms && `${p.rooms} locali`,
    p.neighborhood,
  ].filter(Boolean);
  return (
    <div className="text-[12.5px]" style={{ opacity: 0.7 }}>
      {bits.join(" · ")}
    </div>
  );
}

function PropertyCardEditorial({ p, palette, layout }) {
  return (
    <PropertyLink p={p} className="block group">
      <PropertyCover p={p} palette={palette} />
      <div className="mt-3 flex items-baseline justify-between gap-3">
        <span className="text-[10px] uppercase tracking-[0.28em]" style={{ opacity: 0.6 }}>
          {p.transaction_type === "rent" ? "In affitto" : "In vendita"}
          {p.city ? ` · ${p.city}` : ""}
        </span>
        <span className="text-[14px] font-mono" style={{ color: palette.accent }}>
          {fmtPrice(p.price)}
        </span>
      </div>
      <h3
        className="text-[22px] sm:text-[26px] leading-tight mt-1 mb-1.5"
        style={{ fontFamily: layout.headingFont, ...layout.titleStyle }}
      >
        {p.title}
      </h3>
      <PropertyMeta p={p} palette={palette} />
      <span className="inline-flex items-center gap-1 mt-2 text-[12px] font-medium transition-opacity group-hover:opacity-100" style={{ opacity: 0.7, color: palette.accent }}>
        Scopri <ArrowRight size={11} weight="bold" />
      </span>
    </PropertyLink>
  );
}

function PropertyCardMagazine({ p, idx, palette, layout }) {
  const isEven = idx % 2 === 0;
  return (
    <PropertyLink p={p} className="block group">
      <div className={`grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-8 ${isEven ? "md:[&>div:first-child]:order-2" : ""}`}>
        <div className="md:col-span-7">
          <PropertyCover p={p} palette={palette} aspect="4/3" />
        </div>
        <div className="md:col-span-5 flex flex-col justify-center">
          <div className="text-[10px] uppercase tracking-[0.42em] mb-3" style={{ opacity: 0.6 }}>
            {p.city}
          </div>
          <h3
            className="text-[24px] sm:text-[30px] leading-tight mb-3"
            style={{ fontFamily: layout.headingFont, ...layout.titleStyle }}
          >
            {p.title}
          </h3>
          <PropertyMeta p={p} palette={palette} />
          <div className="mt-4 text-[16px] font-mono" style={{ color: palette.accent }}>
            {fmtPrice(p.price)}
          </div>
        </div>
      </div>
    </PropertyLink>
  );
}

function PropertyCardBento({ p, palette, layout, isHero }) {
  return (
    <PropertyLink
      p={p}
      className={`block group rounded-md overflow-hidden relative ${isHero ? "md:col-span-2 md:row-span-2" : ""}`}
      style={{ border: `1px solid ${palette.border}` }}
    >
      <PropertyCover p={p} palette={palette} aspect={isHero ? "4/3" : "1/1"} rounded="rounded-none" />
      <div className="p-3.5">
        <div className="text-[10px] uppercase tracking-[0.28em] mb-1" style={{ opacity: 0.55 }}>
          {p.transaction_type === "rent" ? "Affitto" : "Vendita"} · {p.city || "—"}
        </div>
        <h3
          className={`leading-tight mb-1.5 ${isHero ? "text-[22px] sm:text-[26px]" : "text-[15px] sm:text-[17px]"}`}
          style={{ fontFamily: layout.headingFont, ...layout.titleStyle }}
        >
          {p.title}
        </h3>
        <div className="flex items-baseline justify-between">
          <PropertyMeta p={p} palette={palette} />
          <span className="text-[13px] font-mono" style={{ color: palette.accent }}>
            {fmtPrice(p.price)}
          </span>
        </div>
      </div>
    </PropertyLink>
  );
}

function PropertyCardMinimal({ p, palette, layout }) {
  return (
    <PropertyLink p={p} className="block group">
      <PropertyCover p={p} palette={palette} aspect="3/2" rounded="rounded-none" />
      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-[0.28em] mb-1" style={{ opacity: 0.6 }}>
          {p.city}{p.neighborhood ? ` · ${p.neighborhood}` : ""}
        </div>
        <h3
          className="text-[24px] sm:text-[28px] leading-tight mb-2"
          style={{ fontFamily: layout.headingFont, ...layout.titleStyle }}
        >
          {p.title}
        </h3>
        <div className="flex items-baseline justify-between gap-3">
          <PropertyMeta p={p} palette={palette} />
          <span className="text-[16px]" style={{ color: palette.accent }}>
            {fmtPrice(p.price)}
          </span>
        </div>
      </div>
    </PropertyLink>
  );
}

function SectionDivider({ palette, label, accentLine = false, soldBadge = null }) {
  // iter v32 r37t · accentLine = pill in colore brand
  // iter v32 r37u · soldBadge = "N venduti negli ultimi 12 mesi"
  // social-proof sopra al divider portfolio
  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-10">
      {soldBadge > 0 && (
        <div className="flex justify-center pt-6 sm:pt-8 pb-1">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] sm:text-[13px] font-semibold"
            style={{
              background: palette.accent,
              color: readableTextOn(palette.accent),
              boxShadow: `0 4px 14px ${palette.accent}33`,
            }}
            data-testid="sold-last-12m-badge"
          >
            <SealCheck size={14} weight="fill" />
            {soldBadge === 1
              ? "1 immobile venduto negli ultimi 12 mesi"
              : `${soldBadge} immobili venduti negli ultimi 12 mesi`}
          </div>
        </div>
      )}
      <div className="flex items-center gap-4 py-6 sm:py-8">
        <div className="flex-1 h-px" style={{ background: palette.border }} />
        {accentLine ? (
          <span
            className="text-[10px] uppercase tracking-[0.32em] px-3 py-1 rounded-full font-semibold"
            style={{ background: `${palette.accent}1A`, color: palette.accent }}
          >
            {label}
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-[0.42em]" style={{ opacity: 0.55 }}>
            {label}
          </span>
        )}
        <div className="flex-1 h-px" style={{ background: palette.border }} />
      </div>
    </div>
  );
}

function ContactFooter({ data, palette, layout, agencyLogo = null }) {
  // iter v32 r37x · User M1460 — CTA "Stima la tua casa" nel footer,
  // porta alla landing pubblica `/stima/{slug}` dell'agente.
  const estimateUrl = data.slug ? `/stima/${data.slug}` : null;
  return (
    <footer
      className="border-t py-12 sm:py-16"
      style={{
        borderColor: palette.border,
        background: palette.bg,
        // iter v32 r37t · accento colore brand in alto del footer
        borderTopColor: palette.accent,
        borderTopWidth: 3,
      }}
    >
      <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
        {/* iter v32 r37x · CTA Stima — banner colore brand all'inizio del footer */}
        {estimateUrl && (
          <Link
            to={estimateUrl}
            data-testid="footer-estimate-cta"
            className="block mb-10 rounded-2xl px-6 py-7 sm:py-8 transition-transform hover:scale-[1.01]"
            style={{
              background: palette.accent,
              color: readableTextOn(palette.accent),
              boxShadow: `0 8px 28px ${palette.accent}55`,
            }}
          >
            <div className="text-[10.5px] uppercase tracking-[0.32em] font-bold opacity-80 mb-2">
              Hai una casa da vendere?
            </div>
            <div
              className="text-[24px] sm:text-[30px] leading-tight mb-3"
              style={{ fontFamily: layout.headingFont, ...layout.titleStyle }}
            >
              Stima la tua casa.
            </div>
            <div className="text-[13.5px] opacity-90 mb-5 max-w-md mx-auto leading-relaxed">
              30 secondi, gratis. Niente nome, niente numero di telefono.
              Ti dico subito quanto vale.
            </div>
            <span
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#1C1C1A] text-[13px] font-bold"
              style={{ color: "#1C1C1A" }}
            >
              Inizia la stima <ArrowRight size={14} weight="bold" />
            </span>
          </Link>
        )}

        <div className="text-[10px] uppercase tracking-[0.42em] mb-4" style={{ color: palette.accent }}>
          Contatta {data.agent_display_name}
        </div>
        <div className="text-[20px] sm:text-[24px] mb-6"
          style={{ fontFamily: layout.headingFont, ...layout.titleStyle }}
        >
          Cerca casa, vendi o vuoi una consulenza?
        </div>
        <div className="flex flex-wrap gap-3 justify-center mb-8">
          <ContactRow data={data} palette={palette} centered />
        </div>
        {data.agency_address && (
          <div className="text-[12px] flex items-center justify-center gap-1.5" style={{ opacity: 0.6 }}>
            <MapPin size={12} weight="regular" />
            {data.agency_address}
          </div>
        )}

        {/* iter v32 r37t · User M1450 — Logo agenzia in fondo (se caricato).
            iter v32 r49g — nome agenzia non mostrato se = nome agente */}
        {agencyLogo && (
          <AgencyLogoFooter
            logo={agencyLogo}
            name={data.agency_name}
            palette={palette}
            agentName={data.agent_display_name}
          />
        )}

        <div className="mt-10 pt-6 border-t flex items-center justify-center gap-1.5 text-[11px]" style={{ borderColor: palette.border, opacity: 0.45 }}>
          <SealCheck size={11} weight="fill" />
          Pagina ufficiale su Pronto · <Link to="/" className="underline">prontoimmo.it</Link>
        </div>
      </div>
    </footer>
  );
}

/** Stat inline per skin Minimal (numero grande + label uppercase). */
function Stat({ value, label, palette, layout }) {
  return (
    <div className="text-center" data-testid={`stat-${label}`}>
      <div
        className="text-[22px] sm:text-[26px] leading-none tracking-tight"
        style={{ fontFamily: layout.headingFont, ...layout.titleStyle, color: palette.fg }}
      >
        {value}
      </div>
      <div
        className="text-[9.5px] uppercase tracking-[0.24em] font-semibold mt-1.5"
        style={{ color: palette.accent, opacity: 0.85 }}
      >
        {label}
      </div>
    </div>
  );
}

/** Logo agenzia con fallback graceful.
 *  iter v32 r49g · User M1454 — Rimosso il box (padding + bg tint + border).
 *  Il logo è già un'identità grafica completa, la cornice lo "imprigionava".
 *  Mostrato solo il logo, monocromo dark (o invert su sfondo scuro), su
 *  sfondo trasparente. Nome agenzia sotto il logo NON viene mostrato se
 *  coincide col nome dell'agente (era un duplicato).
 */
function AgencyLogoFooter({ logo, name, palette, agentName }) {
  const [errored, setErrored] = useState(false);
  if (!logo || errored) return null;
  const showName = name && (!agentName || name.toLowerCase().trim() !== agentName.toLowerCase().trim());
  return (
    <div className="mt-10 flex flex-col items-center gap-3" data-testid="agency-logo-footer">
      <img
        src={logo}
        alt={name || "Logo agenzia"}
        className="object-contain"
        style={{
          maxHeight: 72,
          maxWidth: 220,
          width: "auto",
          height: "auto",
          filter: palette.isDark ? "brightness(0) invert(1)" : "brightness(0)",
          opacity: 0.82,
        }}
        onError={() => setErrored(true)}
      />
      {showName && (
        <div className="text-[10.5px] uppercase tracking-[0.32em]" style={{ opacity: 0.55 }}>
          {name}
        </div>
      )}
    </div>
  );
}

function EmptyState({ palette }) {
  return (
    <div
      className="rounded-md py-14 sm:py-20 text-center"
      style={{ border: `1px dashed ${palette.border}` }}
    >
      <Buildings size={32} weight="light" className="mx-auto mb-3" style={{ opacity: 0.4 }} />
      <div className="text-[14px]" style={{ opacity: 0.75 }}>
        Nessun immobile in vetrina al momento.
      </div>
    </div>
  );
}

function LoadingShell() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7]">
      <div className="text-[13px] text-[#6B6A65] animate-pulse">Caricamento profilo…</div>
    </div>
  );
}

function LoadError() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#FAFAF7] text-center">
      <div>
        <h1 className="font-editorial text-2xl mb-2">Profilo temporaneamente non disponibile</h1>
        <p className="text-sm text-[#6B6A65]">Riprova tra qualche minuto.</p>
      </div>
    </div>
  );
}

function NotFound() {
  // iter v32 r37q — User M1447: 404 più utile per il proprietario.
  // Se ho una sessione attiva, mostro un suggerimento per andare in
  // /dashboard/vetrina-agente a verificare la configurazione.
  const hasSession = typeof document !== "undefined"
    && /vista_jwt|session_token/.test(document.cookie || "");

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#FAFAF7] text-center">
      <div className="max-w-md">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#9A968B] mb-3">404 · Profilo non trovato</div>
        <h1 className="font-editorial text-3xl mb-3">Questo profilo non esiste</h1>
        <p className="text-sm text-[#6B6A65] mb-6">
          L&apos;agente potrebbe aver cambiato il proprio link, oppure il profilo è momentaneamente disattivato.
        </p>
        {hasSession ? (
          <div className="border border-[#A8B89A]/40 bg-[#F1F4ED] rounded-2xl p-4 text-left mb-6">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#5F6B53] font-bold mb-2">
              È il tuo profilo?
            </div>
            <p className="text-[13px] text-[#3F3E3A] mb-3 leading-relaxed">
              Verifica nella tua dashboard:
              <br/>
              <span className="font-medium text-[#1C1C1A]">il link sia esattamente quello che hai salvato</span>{" "}
              · <span className="font-medium text-[#1C1C1A]">la visibilità sia attiva</span>{" "}
              · <span className="font-medium text-[#1C1C1A]">il tuo piano sia Pro</span>.
            </p>
            <Link
              to="/dashboard/vetrina-agente"
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white bg-[#7C8B6E] hover:bg-[#5F6B53] px-3 py-2 rounded-md transition-colors"
            >
              Apri la tua vetrina <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        ) : null}
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1C1C1A] underline"
        >
          Vai su Pronto <ArrowRight size={12} weight="bold" />
        </Link>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   ServicesBlock — "Come posso aiutarti" · 4 servizi standard mostrati
   in card adattati al layout corrente. Differenziato per variant:
   • default   → griglia 4 col con bordo soft
   • luxury    → magazine block centrato con numeri romani
   • moderno   → cards con sfondo accent
   • minimal   → lista verticale serrata
   iter v32 r37r · User M1448 — "Aggiungi anche qualcosa": ogni vetrina
   ora ha una sezione professionale che spiega cosa l'agente fa.
   ═══════════════════════════════════════════════════════════════════ */
function ServicesBlock({ palette, layout, variant = "default" }) {
  const isLuxury = variant === "luxury";
  const isModerno = variant === "moderno";
  const isMinimal = variant === "minimal";
  const eyebrow = isLuxury
    ? "Servizi"
    : isModerno ? "Cosa faccio"
    : isMinimal ? "Servizi"
    : "Come posso aiutarti";

  // iter v33 r64 · User: «riduci il testo, deve essere immediato». Sezione
  // servizi ora e' solo eyebrow + grid icone+titolo (niente H2 lungo di
  // filler tipo "Quattro servizi, una sola persona", niente paragrafo
  // descrittivo sotto ogni servizio). Il cliente scorre, capisce in un
  // secondo, e va oltre.
  return (
    <section
      className="border-t"
      style={{ borderColor: palette.border, background: isModerno ? `${palette.accent}0D` : "transparent" }}
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-10 py-10 sm:py-12">
        <div className="text-[10px] uppercase tracking-[0.42em] mb-6" style={{ opacity: 0.55 }}>
          {eyebrow}
        </div>
        <div
          className={
            isMinimal
              ? "grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 max-w-3xl"
              : "grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4"
          }
        >
          {DEFAULT_SERVICES.map(({ Icon, title }, i) => (
            <div
              key={title}
              className={`${isMinimal ? "border-l-2 pl-3 py-1" : "rounded-lg p-4 sm:p-5 flex flex-col items-center text-center"}`}
              style={{
                borderColor: isMinimal ? palette.accent : palette.border,
                background: isMinimal ? "transparent" : palette.bg,
                border: isMinimal ? undefined : `1px solid ${palette.border}`,
              }}
              data-testid={`service-${i}`}
            >
              <Icon
                size={isLuxury ? 26 : 22}
                weight="light"
                className={isMinimal ? "mb-2" : "mb-2"}
                style={{ color: palette.accent }}
              />
              <h3
                className="text-[13.5px] sm:text-[14.5px] leading-tight"
                style={{ fontFamily: layout.headingFont, ...layout.titleStyle }}
              >
                {title}
              </h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MobileContactBar — Barra contatti sticky in basso visibile SOLO su
   mobile. Sempre raggiungibile col pollice durante lo scroll: zero
   frizione tra chi visita la pagina e il telefono dell'agente.
   iter v32 r37r · User M1448 — Conversion booster.
   ═══════════════════════════════════════════════════════════════════ */
function MobileContactBar({ data, palette }) {
  const wa = buildWa(data.agent_mobile);
  const tel = data.agent_mobile ? `tel:${data.agent_mobile.replace(/[^+0-9]/g, "")}` : null;
  const mail = data.agency_email ? `mailto:${data.agency_email}` : null;
  const hasAny = tel || wa || mail;

  // iter v33 r100 — Fix overlap col CookieBanner (bug LOW da QA r100).
  // Il banner cookie (fixed bottom-0 z-60, ~48-56px altezza) sovrapponeva
  // la pill di contatto sul mobile 390x844 quando il visitatore atterrava
  // per la prima volta. Detect via localStorage `pronto_cookie_consent`.
  const [cookieAccepted, setCookieAccepted] = useState(true);
  useEffect(() => {
    const read = () => {
      try { setCookieAccepted(!!localStorage.getItem("pronto_cookie_consent")); }
      catch { setCookieAccepted(true); }
    };
    read();
    const id = setInterval(read, 800);
    const stop = setTimeout(() => clearInterval(id), 6000);
    window.addEventListener("storage", read);
    return () => {
      clearInterval(id);
      clearTimeout(stop);
      window.removeEventListener("storage", read);
    };
  }, []);

  if (!hasAny) return null;

  // Se cookie banner ancora visibile → push up di 56px (altezza banner)
  const extraBottom = cookieAccepted ? 18 : 74;

  // iter v32 r37x · User M1462 — Riprogettata come floating glassmorphism
  // pill al centro-basso (stile iOS dynamic island), non più una barra
  // intera larga schermo. L'utente ha definito la vecchia "orribile".
  return (
    <div
      className="sm:hidden fixed left-1/2 -translate-x-1/2 z-40 flex items-center gap-1 p-1.5 rounded-full backdrop-blur-xl shadow-[0_8px_28px_rgba(0,0,0,0.18)] transition-all duration-300"
      style={{
        bottom: `calc(env(safe-area-inset-bottom, 0px) + ${extraBottom}px)`,
        background: "rgba(28,28,26,0.88)",
        border: "1px solid rgba(255,255,255,0.12)",
      }}
      data-testid="mobile-contact-pill"
    >
      {tel && (
        <a
          href={tel}
          className="w-11 h-11 rounded-full flex items-center justify-center active:scale-95 transition-transform"
          style={{ background: palette.accent }}
          data-testid="mobile-bar-call"
          aria-label="Chiama"
        >
          <Phone size={18} weight="fill" style={{ color: readableTextOn(palette.accent) }} />
        </a>
      )}
      {wa && (
        <a
          href={wa}
          target="_blank"
          rel="noopener noreferrer"
          className="w-11 h-11 rounded-full flex items-center justify-center active:scale-95 transition-transform"
          style={{ background: "#25D366" }}
          data-testid="mobile-bar-wa"
          aria-label="WhatsApp"
        >
          <WhatsappLogo size={20} weight="fill" style={{ color: "#FFFFFF" }} />
        </a>
      )}
      {mail && (
        <a
          href={mail}
          className="w-11 h-11 rounded-full flex items-center justify-center active:scale-95 transition-transform"
          style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.18)" }}
          data-testid="mobile-bar-email"
          aria-label="Email"
        >
          <EnvelopeSimple size={18} weight="fill" style={{ color: "#FFFFFF" }} />
        </a>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   PublicProfileCTA — banner sticky in basso per agenti non-clienti
   che capitano sul profilo pubblico di un collega. Conversion booster.
   iter v32 r37d · User M1429.
   ═══════════════════════════════════════════════════════════════════ */
const CTA_DISMISS_KEY = "pronto:public-profile-cta:dismissed";

function PublicProfileCTA({ agentName }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Nascondiamo se l'utente ha già un cookie sessione attiva (è già cliente)
    // o se ha già chiuso il banner in passato.
    try {
      const dismissed = window.localStorage?.getItem(CTA_DISMISS_KEY);
      const hasSession = typeof document !== "undefined"
        && /vista_jwt|session_token/.test(document.cookie || "");
      if (dismissed === "1" || hasSession) return;
    } catch { /* */ }
    // Apparizione ritardata per non disturbare la lettura iniziale
    const t = setTimeout(() => setVisible(true), 2200);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const onClose = () => {
    try { window.localStorage?.setItem(CTA_DISMISS_KEY, "1"); } catch { /* */ }
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-[76px] left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-50 animate-in fade-in slide-in-from-bottom-4 duration-500"
      data-testid="public-profile-cta"
    >
      <div className="bg-[#1C1C1A] text-white rounded-2xl shadow-2xl border border-[#2A2A28] overflow-hidden">
        <div className="px-4 py-3 sm:px-5 sm:py-3.5 flex items-center gap-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-[#7C8B6E]/20 text-[#A8B89A] flex items-center justify-center">
              <Sparkle size={16} weight="fill" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            {/* iter v33 r64 · User: «troppo testo, uno si perde via». Ridotto
                il retargeting a UNA riga essenziale: eyebrow + CTA. Il paragrafo
                lungo su "annunci, foto migliorate, vetrina condivisibile" era
                filler ridondante che rubava spazio senza spostare il click. */}
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#A8B89A] leading-tight">
              Sei un agente?
            </div>
            <div className="text-[12.5px] text-white/90 leading-tight mt-0.5">
              Fatti anche tu la vetrina.
            </div>
          </div>
          <Link
            to="/register"
            className="flex-shrink-0 inline-flex items-center gap-1 bg-[#A8B89A] hover:bg-[#7C8B6E] text-[#1C1C1A] hover:text-white px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors whitespace-nowrap"
            data-testid="public-profile-cta-register"
          >
            Prova <ArrowRight size={12} weight="bold" />
          </Link>
          <button
            onClick={onClose}
            aria-label="Chiudi"
            className="flex-shrink-0 w-6 h-6 rounded-full text-white/40 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors"
            data-testid="public-profile-cta-close"
          >
            <X size={13} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
